// becoming.to — one morning at a time, and a whole one to wander if you want it.
//
// There is no chrome: the card is the whole interface. Drag it sideways to
// travel through days, up and down to wander that day's trail, hold it to carry
// the words out with you, double-tap to keep it — kept words become motes of
// light in your morning and stay there, and touching one brings it back. Tap the
// word above an affirmation to tune the trail to it. Nothing on screen explains
// any of this; the card moves under your finger, which is the only teacher it
// needs.

import { fetchTrail, platformOrigin, type Affirmation } from "./api.js";
import * as sound from "./audio.js";
import { mulberry32 } from "./pool/core/prng.js";
import { bindGestures } from "./pool/shell/gestures.js";
import * as keeps from "./keeps.js";
import { createPulse, type PulseClient } from "./pulse.js";
import { share } from "./share.js";
import { applyLight, type Light } from "./theme.js";
import { cardWeight, requestTiltPermission, startTilt } from "./tilt.js";
import { createTurn, type Turn } from "./turn.js";
import { unveil, type Unveil } from "./unveil.js";
import { startWorld, type World } from "./world.js";

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function shiftDay(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function tap(pattern: number | number[]): void {
  // Haptics are the third sense; absent on desktop, and that is fine.
  if (typeof navigator.vibrate === "function") navigator.vibrate(pattern);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** The drag distance (px) that maps to a full ±1 turn.progress() — matches
 * gestures.ts's own COMMIT_DISTANCE (the point a drag actually commits a
 * travel). Duplicated here rather than imported: that constant isn't
 * exported, and the vendored shell (frontend/src/pool/**) is never
 * hand-edited in this repo — keep this in sync if it moves upstream. */
const TURN_DRAG_PX = 64;

interface State {
  day: string;
  dayNumber: number;
  trail: readonly Affirmation[];
  /** Where along the day's trail we are; 0 is the day's card. */
  step: number;
  /** When set, the trail is tuned to this category alone. */
  tuned: string | null;
}

const state: State = { day: isoToday(), dayNumber: 0, trail: [], step: 0, tuned: null };
let dawn: World;
let light: Light;
let pulse: PulseClient;
/** Law 1: the sideways drag's page-turn feel and the fixed-pace completion
 * that follows a committed one — see turn.ts's module header. */
let turn: Turn;
/** The affirmation text currently unveiling, if any — any user gesture
 * interrupts it via `finish()` (law 4: pacing invites, it never imprisons). */
let activeUnveil: Unveil | null = null;
/** Law 3: the current card's tilt listener — stopped and replaced on every
 * paint, so an old card's device-orientation binding never sways a new one. */
let activeTilt: { stop(): void } | null = null;

/** Shared by every gesture entry point: a mid-unveil affirmation jumps
 * straight to fully revealed the moment the visitor does anything at all. */
function interruptUnveil(): void {
  activeUnveil?.finish();
}
/** Seeded stream for ripple placement — never Math.random (determinism
 * discipline). Seeded fresh each load from today's real date, independent of
 * whatever day the visitor is browsing. */
let pulseRand: () => number;

/** A stable per-day seed so the world's layout is deterministic within a day. */
function daySeed(iso: string): number {
  let h = 2166136261;
  for (let i = 0; i < iso.length; i++) {
    h ^= iso.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** The card the visitor is currently looking at, but only when it is the
 * day's own card (step 0, untuned) — pulse.ts's "daily resonance" is
 * specifically the day's card rising in count while it's the one on screen,
 * not whatever affirmation a wander or tune happens to have surfaced. */
function dailyCardId(): string | null {
  if (state.step !== 0 || state.tuned) return null;
  return currentAffirmation()?.card_id ?? null;
}

/** A ripple's x, drawn from the pulse-seeded stream. */
function seededX(): number {
  return pulseRand();
}

/** A ripple's y, within the water's lower band (world.ts clips its water
 * painter to roughly [0.72, 1] of the square). */
function lowerBandY(): number {
  return 0.76 + pulseRand() * 0.2;
}

/** Pulse events drive `world.setLiveliness` from ripple recency, not raw
 * counts: a burst lifts the shimmer target, held briefly, then settled back
 * to still. This is the simplest of the two options the brief allows —
 * app.ts owns one setTimeout per bump; world.ts already eases toward
 * whatever target it's given each frame, so it needs nothing further. */
const LIVELINESS_HOLD_MS = 6_000;
let livelinessTimer: ReturnType<typeof setTimeout> | null = null;
function bumpLiveliness(): void {
  dawn.setLiveliness(1);
  if (livelinessTimer) clearTimeout(livelinessTimer);
  livelinessTimer = setTimeout(() => dawn.setLiveliness(0), LIVELINESS_HOLD_MS);
}

/** The trail as it stands — the whole walk, or just the tuned part of it. */
function walk(): readonly Affirmation[] {
  if (!state.tuned) return state.trail;
  const only = state.trail.filter((a) => a.category === state.tuned);
  // Tuning to a category that empties the trail would be a dead end; the
  // untuned walk is always the floor.
  return only.length > 0 ? only : state.trail;
}

function currentAffirmation(): Affirmation | null {
  return walk()[state.step] ?? null;
}

/** How full this day's dawn is compared to today's: 1 today, lowering back. */
function dayLight(): number {
  const today = new Date(`${isoToday()}T00:00:00Z`).getTime();
  const then = new Date(`${state.day}T00:00:00Z`).getTime();
  const daysBack = Math.round((today - then) / 86_400_000);
  // Wandering deep into a morning dims it a little too: the day's card is the
  // brightest thing here, which is what makes it the day's card.
  const wandered = Math.min(state.step * 0.02, 0.2);
  return Math.max(0.12, 1 - daysBack * 0.06 - wandered);
}

function canTravel(delta: number): boolean {
  const target = shiftDay(state.day, delta);
  if (target > isoToday()) return false;
  return state.dayNumber + delta >= 1;
}

function canWander(delta: number): boolean {
  const next = state.step + delta;
  return next >= 0 && next < walk().length;
}

/** Paint the current affirmation, or an explicit `affirmation` override.
 * `instant` skips the phrase-by-phrase unveil entirely — used only for a kept
 * card re-expanding from its mote, which reads as "the words are still here",
 * not as a re-reveal. The `affirmation` override is how a recalled mote paints
 * itself directly from its keep record (see `tapped()`) without disturbing
 * `state` at all — the mote may belong to a day whose trail was never loaded
 * here. becoming carries no author, so there is no attribution step to fade. */
function paint(opts: { instant?: boolean; affirmation?: keeps.Keep } = {}): void {
  const card = el<HTMLElement>("card");
  const affirmationEl = el<HTMLElement>("affirmation");
  interruptUnveil();
  activeUnveil = null;
  activeTilt?.stop();
  activeTilt = null;
  const affirmation = opts.affirmation ?? currentAffirmation();
  if (!affirmation) {
    affirmationEl.textContent = "The morning is out of reach right now.";
    el<HTMLElement>("category").textContent = "";
    card.classList.add("risen");
    return;
  }
  const category = el<HTMLElement>("category");
  category.textContent = affirmation.category;
  category.classList.toggle("tuned", state.tuned === affirmation.category);
  card.classList.toggle("kept", keeps.isKept(affirmation));
  dawn.setLight(dayLight());
  // Law 3: the words' own weight sets how much they can sway under the
  // device's tilt — a fresh binding per card, on the card element itself so
  // the .affirmation child inherits the CSS vars it writes.
  activeTilt = startTilt(card, cardWeight(affirmation.text));

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (opts.instant) {
    affirmationEl.textContent = affirmation.text;
  } else {
    activeUnveil = unveil(affirmationEl, affirmation.text, {
      reducedMotion,
      onPhrase(index, total) {
        tap(18);
        sound.phrase(index, total);
      },
    });
  }
  requestAnimationFrame(() => card.classList.add("risen"));
}

async function load(direction = 0): Promise<void> {
  const card = el<HTMLElement>("card");
  card.classList.remove("risen");
  const trail = await fetchTrail(state.day === isoToday() ? undefined : state.day);
  if (!trail) {
    // A failed/empty fetch leaves `state.dayNumber` untouched here — it does
    // NOT need correcting. Any delta that led to this load already shifted
    // `state.dayNumber` in lockstep with `state.day` synchronously back in
    // `travel()`'s `done` callback, before `load()` was even called, so the
    // two are already consistent with each other. There is simply no
    // server-authoritative value to re-sync to when the fetch didn't land.
    state.trail = [];
    paint();
    return;
  }
  state.day = trail.date;
  state.dayNumber = trail.day;
  state.trail = trail.trail;
  state.step = 0;
  if (direction !== 0) {
    dawn.surface(direction);
    sound.surface(trail.day);
    tap(8);
  }
  paint();
}

function travel(delta: number): void {
  if (!canTravel(delta)) return;
  // The turn's own sound (paper()) fires inside turn.commit; the haptic
  // answers it here, at the same instant — both mark the moment a turn
  // commits, not the moment it finishes.
  tap([12, 60, 8]);
  // `state.day` only advances once THIS turn's animation has actually
  // finished (turn.ts's `done`) — not up front. A rapid second travel while
  // this one is still turning queues behind it in turn.ts and reads the
  // day fresh when its own turn is committed, so the trail displayed always
  // matches the turn that just played, never skipping ahead of it.
  //
  // The `canTravel` check above ran against `state.day`/`state.dayNumber` as
  // they stood at enqueue time, which is stale for any commit queued behind
  // others still playing — earlier queued commits will have shifted both
  // underneath this one before it gets its turn. Re-check right here, at
  // execution time, against the now-current values: if this turn would now
  // cross the epoch (or today), the card still visually completes its turn —
  // turn.ts's `finishCommit` already reset it flat before calling `done` —
  // but nothing loads and neither `state.day` nor `state.dayNumber` moves
  // past the bound.
  //
  // `state.dayNumber` shifts SYNCHRONOUSLY, in the same statement block as
  // `state.day` — not after `load()`'s awaited fetch resolves. `load()` is a
  // network round-trip of unbounded latency; the turn queue that runs these
  // `done` callbacks fires at a fixed MIN_TURN_MS (420ms) cadence regardless
  // of how long any earlier fetch is taking. If `dayNumber` only advanced
  // once a slow fetch resolved, a queued backward `done` landing before that
  // fetch settles would re-check `canTravel` against a `dayNumber` that
  // hasn't caught up to `state.day` yet, letting the epoch bound (`dayNumber
  // + delta >= 1`) pass when it shouldn't. Shifting both in lockstep here
  // means the bound check is exact at the moment it runs, independent of any
  // in-flight fetch. A successful `load()` still overwrites `state.dayNumber`
  // from the server response afterward — that's the authoritative re-sync;
  // this lockstep shift is only ever a step ahead of it, never a
  // replacement, and if `load()` fails (or the trail comes back null) the
  // value it computed here is what's left standing, still consistent with
  // `state.day` since both moved together.
  turn.commit(delta as 1 | -1, () => {
    if (!canTravel(delta)) return;
    state.day = shiftDay(state.day, delta);
    state.dayNumber += delta;
    void load(delta);
  });
}

function wander(delta: number): void {
  if (!canWander(delta)) return;
  state.step += delta;
  const card = el<HTMLElement>("card");
  card.classList.remove("risen");
  dawn.surface(0);
  sound.surface(state.dayNumber + state.step);
  tap(5);
  paint();
}

/** Tune the trail to these words' category, or let it open back up. */
function tune(category: string): void {
  state.tuned = state.tuned === category ? null : category;
  state.step = 0;
  sound.surface(category.length);
  tap(6);
  const card = el<HTMLElement>("card");
  card.classList.remove("risen");
  paint();
}

function refreshSky(): void {
  dawn.setKeeps(keeps.list());
}

function keep(): void {
  const affirmation = currentAffirmation();
  if (!affirmation) return;
  const card = el<HTMLElement>("card");
  const nowKept = keeps.toggle({ ...affirmation, day: state.dayNumber, date: state.day });
  refreshSky();
  card.classList.toggle("kept", nowKept);
  if (nowKept) {
    // The light leaves the words and climbs to its place in the morning.
    const box = card.getBoundingClientRect();
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    dawn.rise(cx, cy);
    // Law 5 compression: the card itself collapses toward the very point its
    // mote lands (dawn.landingPoint), synced with the riser — the words draw
    // inward and are released as the mote, then settle back kept. Reduced
    // motion skips the flourish (the mote still registers): a single calm
    // frame, no collapse.
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const land = dawn.landingPoint();
    if (land && !reducedMotion) {
      card.style.setProperty("--keep-dx", `${(land.x - cx).toFixed(1)}px`);
      card.style.setProperty("--keep-dy", `${(land.y - cy).toFixed(1)}px`);
      // Restart the animation cleanly even on a re-keep in quick succession.
      card.classList.remove("keeping");
      void card.offsetWidth;
      card.classList.add("keeping");
    }
    sound.carried();
    tap([4, 30, 8]);
    pulse.recordKeep(affirmation.card_id);
  } else {
    sound.released();
    tap(12);
  }
}

/** A tap on the open morning: if it found a kept mote, bring those words back.
 * The mote may belong to a day whose trail was never loaded into `state` —
 * every kept mote is re-expandable, not just today's — so this resolves the
 * full record straight from the keeps module rather than searching
 * `state.trail`. */
function tapped(x: number, y: number): void {
  const mote = dawn.moteAt(x, y);
  if (!mote) return;
  const record = keeps.list().find((k) => keeps.sameCard(k, mote));
  if (!record) return;
  const card = el<HTMLElement>("card");
  card.classList.remove("risen");
  sound.surface(record.day);
  tap(6);
  // These words already lived on screen once; recalling them from the morning
  // is a return, not a first reading, so they arrive whole (no unveil). Recall
  // is a view onto the kept mote, not a navigation: state.day/dayNumber/
  // trail/step/tuned are left exactly as they stood, so the next travel or
  // wander resumes from wherever the visitor's own day-trail actually was —
  // even when the recalled mote was kept on a different day entirely.
  paint({ instant: true, affirmation: record });
}

async function carry(): Promise<void> {
  const affirmation = currentAffirmation();
  if (!affirmation) return;
  const card = el<HTMLElement>("card");
  const outcome = await share(affirmation, state.dayNumber, light);
  if (outcome === "failed") {
    // A hold always gets an answer. The breath is let go with nothing in it:
    // honest about what happened, still spoken in the world's language.
    card.classList.add("released");
    sound.released();
    tap(30);
    setTimeout(() => card.classList.remove("released"), 800);
    return;
  }
  card.classList.add("carried");
  dawn.surface(0);
  sound.carried();
  tap([6, 40, 10]);
  setTimeout(() => card.classList.remove("carried"), 800);
}

/** One small drift on arrival: the card shows it can move, so nothing has to say so. */
function hint(card: HTMLElement): void {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  setTimeout(() => {
    card.classList.add("hinting");
    setTimeout(() => card.classList.remove("hinting"), 900);
  }, 1400);
}

export function main(): void {
  light = applyLight();
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  dawn = startWorld(el<HTMLElement>("dawn"), { seed: daySeed(isoToday()), reducedMotion });
  dawn.setHue(light.core, light.rim);
  refreshSky();

  // Ambient co-presence (law 2): a pulse blackout is still water, never an
  // error — see pulse.ts's module header for the degradation contract.
  pulseRand = mulberry32(daySeed(`${isoToday()}:pulse-ripple`));
  pulse = createPulse(platformOrigin(), "becoming", dailyCardId);
  pulse.onRemoteKeep((n) => {
    const count = Math.min(n, 5);
    for (let i = 0; i < count; i++) dawn.ripple(seededX(), lowerBandY());
    if (count > 0) bumpLiveliness();
  });
  pulse.onDailyResonance(() => {
    sound.chime();
    bumpLiveliness();
  });
  pulse.start();
  const card = el<HTMLElement>("card");
  const stage = el<HTMLElement>("stage");
  turn = createTurn(card);

  // Law 5: once the keep collapse (styles.css @keyframes keeping) has played,
  // drop the class so the kept card settles back into place, still readable.
  card.addEventListener("animationend", (ev) => {
    if (ev.animationName === "keeping") card.classList.remove("keeping");
  });

  // The category is a word on the card, not a control: touching it tunes the
  // trail. It stops the gesture here so tuning never reads as a stray tap.
  const category = el<HTMLElement>("category");
  category.addEventListener("pointerdown", (ev) => ev.stopPropagation());
  category.addEventListener("click", () => {
    const affirmation = currentAffirmation();
    if (affirmation) tune(affirmation.category);
  });

  // Law 4: any gesture starting anywhere on the stage skips a mid-unveil
  // affirmation straight to fully revealed — added once here rather than
  // inside every handler below, since it needs to fire before any of them
  // decide what the gesture even is.
  //
  // Law 3 piggybacks on the same listener: iOS's DeviceOrientation gate can
  // only ever be asked from inside a user gesture, and the first touch the
  // visitor makes anywhere on the stage is the earliest, least intrusive one
  // available — never a prompt on load. `requestTiltPermission` memoizes its
  // own result, but this flag keeps a long session from re-invoking it on
  // every later touch for no reason.
  let tiltPermissionAsked = false;
  stage.addEventListener("pointerdown", () => {
    interruptUnveil();
    if (!tiltPermissionAsked) {
      tiltPermissionAsked = true;
      void requestTiltPermission();
    }
  });

  // The vendored gesture shell (gestures.ts) answers Arrow*/Enter/Space/k
  // itself, via its own document-level keydown listener — but it doesn't
  // know about the unveil, so a mid-unveil keypress on any of those would
  // otherwise leave the animation running while the keypress's own action
  // (travel/wander/carry/keep) proceeds. Capture-phase on document always
  // runs before that bubble-phase listener — same discipline as the stage
  // pointerdown catch-all above — so it's the OLD unveil that gets
  // interrupted, not a new one that started synchronously in between.
  const GESTURE_KEYS = new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Enter", " "]);
  document.addEventListener(
    "keydown",
    (ev) => {
      if (!activeUnveil) return;
      if (GESTURE_KEYS.has(ev.key) || ev.key.toLowerCase() === "k") interruptUnveil();
    },
    { capture: true },
  );

  // bindGestures now returns an unbind fn (upstream pool shell is canonical).
  // The app mounts the world exactly once and never unmounts, so we hold the
  // handle for a future teardown path rather than calling it now.
  const _unbindGestures = bindGestures(
    stage,
    {
      drag(dx, dy) {
        card.classList.toggle("dragging", dx !== 0 || dy !== 0);
        // Sideways is the page turn (turn.ts owns the card's transform for
        // that axis via CSS vars); up/down is the wander, tracked directly.
        // gestures.ts always sends a final (0, 0) once the finger lifts,
        // BEFORE it decides what the gesture committed to — turn.progress
        // is deliberately left untouched on that call, so whatever the drag
        // last reached is still there for commit()/cancel() to animate from,
        // instead of being snapped to rest a beat before they run.
        //
        // A vertical wander drag writes `card.style.transform` inline, which
        // wins the cascade over `.card`'s class-level rule unconditionally —
        // including over the rotateY/translateX that rule reads from
        // turn.ts's --turn-rot/--turn-x while a commit is playing. Skip the
        // inline write for as long as a turn is committing (queued or in
        // flight) so it can never clobber that mid-commit transform; the
        // vertical drag simply has no visual effect until the turn settles.
        if (dy !== 0 && !turn.isCommitting()) {
          card.style.transform = `translate(0px, ${dy}px)`;
        } else if (dx !== 0) {
          card.style.transform = "";
          turn.progress(clamp(-dx / TURN_DRAG_PX, -1, 1));
        } else {
          card.style.transform = "";
        }
      },
      travel,
      wander,
      gathering(progress) {
        dawn.setGather(progress);
        card.style.setProperty("--gather", String(progress));
        if (progress === 1) {
          sound.drawIn();
          tap(4);
        }
      },
      gathered() {
        dawn.setGather(0);
        card.style.setProperty("--gather", "0");
        void carry();
      },
      kept: keep,
      tapped,
      settle() {
        dawn.setGather(0);
        card.style.setProperty("--gather", "0");
        turn.cancel();
      },
    },
    { canTravel, canWander },
  );
  // Held only so a future unmount can unbind; the single-mount flow never does.
  void _unbindGestures;

  // The same verbs, spoken to assistive tech. Visually there is nothing here —
  // the world is the interface — but nobody is locked out of it. Any of them
  // is as much a gesture as a drag, so the same unveil-skip applies.
  // Capture-phase: a plain bubble-phase listener here would fire AFTER the
  // buttons' own click handlers below (e.g. a11y-wander's synchronous
  // wander() → paint(), which starts a brand-new unveil before the event
  // finishes dispatching) and would interrupt that NEW unveil instead of the
  // old one — revealing it instantly. Capture-phase ancestor listeners run
  // before at-target listeners, so this always interrupts the OLD unveil.
  el<HTMLElement>("a11y-prev").parentElement?.addEventListener("click", interruptUnveil, { capture: true });
  el<HTMLButtonElement>("a11y-prev").addEventListener("click", () => travel(-1));
  el<HTMLButtonElement>("a11y-next").addEventListener("click", () => travel(1));
  el<HTMLButtonElement>("a11y-wander").addEventListener("click", () => wander(1));
  el<HTMLButtonElement>("a11y-keep").addEventListener("click", keep);
  el<HTMLButtonElement>("a11y-share").addEventListener("click", () => void carry());

  // The room keeps your hours even if you leave it open past dawn.
  setInterval(() => {
    light = applyLight();
    dawn.setHue(light.core, light.rim);
  }, 600_000);

  hint(card);
  void load();
}

main();
